import React, { useEffect, useState } from "react";
import { MapPin, Navigation, Mail } from "lucide-react";
import { supabase } from "../utils/supabaseClient";
import "../styles/ContactUsComponent.css";
import "../styles/LocationsList.css";

const ContactUsComponent = () => {
  const [offices, setOffices] = useState([]);
  const [headOffice, setHeadOffice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch head office data
        const { data: headOfficeData, error: headOfficeError } = await supabase
          .from("head_office")
          .select("*")
          .single();

        if (headOfficeError) {
          throw headOfficeError;
        }

        // Fetch other offices data
        const { data: officesData, error: officesError } = await supabase
          .from("offices")
          .select("*")
          .order("type", { ascending: true });

        if (officesError) {
          throw officesError;
        }

        setHeadOffice(headOfficeData);
        setOffices(officesData || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">Error: {error}</div>;
  if (!headOffice)
    return <div className="error">Head office data not found</div>;

  const branchOffices = offices.filter((office) => office.type === "branch");
  const regionalOffices = offices.filter(
    (office) => office.type === "regional"
  );

  const handleGetDirections = () => {
    if (headOffice.maps_link) {
      window.open(headOffice.maps_link, "_blank");
    }
  };

  const handleMapDirections = (mapsLink) => {
    window.open(mapsLink, "_blank");
  };

  return (
    <div className="contact">
      <h1 className="contact__title">Contact Us</h1>
      <div className="contact__container">
        <div className="contact__info">
          <div className="contact__card">
            <div className="contact__icon">
              <MapPin size={24} strokeWidth={2} />
            </div>
            <div className="contact__details">
              <h3>Infinipoint Technologies Pvt Ltd</h3>
              <p className="head-office">Head Office</p>
              <p>{headOffice.address}</p>
              {headOffice.maps_link && (
                <button
                  className="contact__directions-btn"
                  onClick={handleGetDirections}
                >
                  <Navigation size={16} strokeWidth={2.5} />
                  <span>Get Directions</span>
                </button>
              )}
            </div>
          </div>

          <div className="contact__card">
            <div className="contact__icon">
              <Mail size={24} strokeWidth={2} />
            </div>
            <div className="contact__details">
              <h3>Email Us</h3>
              <a href={`mailto:${headOffice.email}`} className="contact__email">
                {headOffice.email}
              </a>
            </div>
          </div>
        </div>

        <div className="contact__map">
          <iframe
            src={
              headOffice.maps_link
                ? `https://www.google.com/maps/embed?pb=${
                    headOffice.maps_link.split("?pb=")[1]
                  }`
                : "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3782.2623029725547!2d73.78574007501825!3d18.559331982577583!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2bf40bef092f1%3A0x48c508ccaa4ef9a!2sInfinipoint%20Technologies%20Pvt%20Ltd!5e0!3m2!1sen!2sin!4v1690364358949!5m2!1sen!2sin"
            }
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Organization Location"
          ></iframe>
        </div>
      </div>

      <div className="locations-container">
        <div className="office-section">
          <h2>Regional Offices</h2>
          <div className="offices-grid">
            {regionalOffices.map((office) => (
              <div key={office.id} className="office-card">
                <div className="office-card-content">
                  <h3>{office.city}</h3>
                  <p className="office-card-address">{office.address}</p>
                  <div className="contact-info">
                    <p>
                      <i className="fa-brands fa-whatsapp fa-lg"></i>
                      <a
                        href="https://api.whatsapp.com/send/?phone=919168617657&type=phone_number"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="contact-phone"
                      >
                        +91-9168617657
                      </a>
                    </p>
                    <p>
                      <i className="fa-regular fa-envelope-open fa-lg"></i>
                      <a
                        href={`mailto:${office.email}`}
                        className="contact-email"
                      >
                        {office.email}
                      </a>
                    </p>
                  </div>
                </div>
                {office.maps_link && (
                  <button
                    className="get-directions-button"
                    onClick={() => handleMapDirections(office.maps_link)}
                  >
                    <MapPin size={22} strokeWidth={2} />
                    <span>Show on Maps</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="office-section">
          <h2>Branch Offices</h2>
          <div className="offices-grid">
            {branchOffices.map((office) => (
              <div key={office.id} className="office-card">
                <h3>{office.city}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUsComponent;
